import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDuration, mediaUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Boxes, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get(`/packages/${id}`).then((r) => setPkg(r.data)).catch(() => setPkg(false));
  }, [id]);

  const buy = async () => {
    if (!user) { toast.error("Please log in as a customer to buy."); navigate("/login?next=/packages/" + id); return; }
    if (user.role !== "customer") { toast.error("Only customers can buy packages."); return; }
    setBuying(true);
    try {
      const { data } = await api.post(`/packages/${id}/checkout`, { origin_url: window.location.origin });
      if (data.free) { toast.success("Package added!"); navigate("/my-packages"); }
      else if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBuying(false);
    }
  };

  if (pkg === null) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  if (pkg === false) return <div className="py-20 text-center text-muted-foreground" data-testid="package-not-found">Package not found or no longer available.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="package-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="package-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-[1fr_340px] gap-10">
        <div>
          {pkg.cover_url && (
            <img src={mediaUrl(pkg.cover_url)} alt={pkg.name} className="w-full h-56 object-cover rounded-sm border border-border mb-6" data-testid="package-cover-image" />
          )}
          <div className="flex items-center gap-2 text-primary"><Boxes className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Package</span></div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">{pkg.name}</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">{pkg.description || "No description provided."}</p>

          <h2 className="font-display text-2xl font-semibold mt-8">What's included</h2>
          <div className="mt-4 space-y-3">
            {pkg.items.map((i) => (
              <div key={i.service_id} className="flex items-center justify-between border border-border rounded-sm bg-white p-4" data-testid={`pkg-item-${i.service_id}`}>
                <div>
                  <div className="font-medium">{i.service_title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">by {i.provider_name} <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(i.duration_minutes)}</span></div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-primary">${i.discounted_price}</div>
                  {i.discounted_price < i.base_price && <div className="text-xs text-muted-foreground line-through">${i.base_price}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit border border-border rounded-sm bg-white p-6 hard-shadow">
          <h3 className="font-display text-xl font-semibold">Package summary</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Regular price</span><span className="line-through">${pkg.total_base}</span></div>
            {pkg.total_savings > 0 && <div className="flex justify-between text-primary"><span className="flex items-center gap-1"><Sparkles className="w-4 h-4" />You save</span><span>${pkg.total_savings}</span></div>}
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-border">
            <span className="text-muted-foreground">Total</span>
            <span className="font-display text-2xl font-semibold text-primary" data-testid="package-total">${pkg.total_price}</span>
          </div>
          <Button onClick={buy} disabled={buying} className="w-full mt-5 rounded-sm" data-testid="buy-package-btn">
            {buying ? "Processing…" : pkg.total_price > 0 ? `Buy package $${pkg.total_price}` : "Get package free"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">Pay once, then schedule each service separately from <Link to="/my-packages" className="text-primary">My Packages</Link>.</p>
        </div>
      </div>
    </div>
  );
}
